import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import DateTimePicker from 'src/components/common/DateTimePicker';
import { AsyncMultiSelectDropdown } from 'src/components/common/AsyncMultiSelectDropdown';
import { useInterviewSchedule } from 'src/services/api/queries/candidateQueries';
import { mapScheduleToFormValues } from './actionPayloadSchemas';

// Generic form modal for the handful of senior_level_flow actions that need
// structured input beyond "which candidates" (see actionPayloadSchemas.js) —
// one modal implementation shared by every such action, same rationale as
// GenericFlowAction itself covering ~15 action keys.
const ActionPayloadModal = ({
  open,
  onOpenChange,
  schema,
  candidateCount,
  secMappingId,
  isSubmitting,
  onSubmit,
}) => {
  const [values, setValues] = useState({});

  // Tracks whether the user has actually changed anything since the modal
  // opened — set only inside handleChange below, so reschedule_slot's
  // prefill (setValues driven by the fetched schedule, not by the user)
  // never trips it. Backs the onPointerDownOutside guard on <Dialog>
  // further down: an accidental click outside a form the user has started
  // filling in (or a prefilled reschedule they've started editing)
  // shouldn't silently discard it the way an untouched one safely can.
  const isDirtyRef = useRef(false);

  // Start each opening from a clean form rather than carrying over the
  // previous action's (or a cancelled attempt's) values.
  useEffect(() => {
    if (open) {
      setValues({});
      isDirtyRef.current = false;
    }
  }, [open]);

  // reschedule_slot's prefill (see actionPayloadSchemas.js's
  // `prefillFromSchedule`) — only meaningful for a single candidate, same
  // gating UpdateProfileDetailsModal already uses for its own skill-match
  // prefill. Called unconditionally (before the `!schema` early return
  // below) since hooks can't follow a conditional return.
  const shouldPrefill = !!schema?.prefillFromSchedule;
  const {
    data: scheduleResponse,
    isFetching: scheduleFetching,
    refetch: refetchSchedule,
  } = useInterviewSchedule(secMappingId, {
    enabled: open && shouldPrefill,
  });
  const scheduleData = scheduleResponse?.data;

  // Forces a real network hit every time the modal opens. This modal stays
  // mounted the whole time (GenericFlowAction renders it unconditionally,
  // only `open` toggles) — confirmed live that `enabled` flipping
  // false→true on an already-mounted useQuery does NOT count as a fresh
  // "mount" to React Query, so neither the default staleTime nor
  // `refetchOnMount: 'always'` (tried first; verified live it changed
  // nothing — still exactly one request across two opens) causes a
  // refetch here. An explicit imperative refetch() is what actually forces
  // one, bypassing staleTime/mount-detection entirely. Reported live as
  // "not calling every time" — real fix requires this, not a query-option
  // tweak.
  useEffect(() => {
    if (open && shouldPrefill && secMappingId) refetchSchedule();
    // Only the `open` transition should trigger this — shouldPrefill/
    // secMappingId/refetchSchedule are already reflected in the `enabled`
    // gate and the closure over refetchSchedule itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Guards the prefill so it fires once per open, right when the fetch
  // resolves — mirrors UpdateProfileDetailsModal's own prefilledRef.
  //
  // Waits on scheduleFetching too, not just scheduleData's presence: on
  // reopen, React Query hands back the PREVIOUS open's cached slot
  // immediately, before the refetch triggered above has actually landed.
  // Firing on that first, stale scheduleData would lock prefilledRef and
  // silently swallow the real response that arrives moments later — this
  // was the second half of the same reported bug ("calls, but the
  // reopened modal never shows what it fetched"). Waiting for
  // scheduleFetching to settle back to false means scheduleData is always
  // the just-fetched value by the time this reads it.
  const prefilledRef = useRef(false);
  useEffect(() => {
    if (open) prefilledRef.current = false;
  }, [open]);

  useEffect(() => {
    if (
      !open ||
      !shouldPrefill ||
      scheduleFetching ||
      !scheduleData ||
      prefilledRef.current
    ) {
      return;
    }
    prefilledRef.current = true;
    mapScheduleToFormValues(scheduleData).then((prefillValues) => {
      setValues((prev) => ({ ...prefillValues, ...prev }));
    });
  }, [open, shouldPrefill, scheduleData, scheduleFetching]);

  if (!schema) return null;

  const handleChange = (name, value) => {
    isDirtyRef.current = true;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const isFieldComplete = (field) => {
    if (!field.required) return true;
    const value = values[field.name];
    if (field.type === 'date-time') {
      return !!(value && value.date && value.time);
    }
    if (field.type === 'async-multi-select') {
      return Array.isArray(value) && value.length > 0;
    }
    if (field.type === 'duration') {
      if (value === 'custom') {
        const customValue = values[`${field.name}_custom`];
        return !!customValue && Number(customValue) > 0;
      }
      return !!value;
    }
    return !!value;
  };

  const isComplete = schema.fields.every(isFieldComplete);

  const handleSubmit = () => {
    if (!isComplete) return;
    onSubmit(schema.buildPayload(values));
  };

  const renderField = (field) => {
    if (field.type === 'select') {
      return (
        <Select
          value={values[field.name] || ''}
          onValueChange={(value) => handleChange(field.name, value)}
        >
          <SelectTrigger id={field.name}>
            <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (field.type === 'duration') {
      const value = values[field.name] || '';
      const isCustom = value === 'custom';
      return (
        <div className="space-y-2">
          <Select
            value={value}
            onValueChange={(next) => handleChange(field.name, next)}
          >
            <SelectTrigger id={field.name}>
              <SelectValue placeholder="Select duration" />
            </SelectTrigger>
            <SelectContent>
              {field.options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
              <SelectItem value="custom">Custom…</SelectItem>
            </SelectContent>
          </Select>
          {isCustom && (
            <Input
              type="number"
              min="1"
              step="1"
              placeholder="Enter minutes"
              value={values[`${field.name}_custom`] || ''}
              onChange={(e) =>
                handleChange(`${field.name}_custom`, e.target.value)
              }
            />
          )}
        </div>
      );
    }
    if (field.type === 'date-time') {
      return (
        <DateTimePicker
          dateLabel={null}
          timeLabel={null}
          date={values[field.name]?.date}
          time={values[field.name]?.time || ''}
          minDate={field.minDate}
          onDateChange={(date) =>
            handleChange(field.name, { ...values[field.name], date })
          }
          onTimeChange={(time) =>
            handleChange(field.name, { ...values[field.name], time })
          }
        />
      );
    }
    if (field.type === 'async-multi-select') {
      return (
        <AsyncMultiSelectDropdown
          fetcher={field.fetcher}
          getOptionLabel={field.getOptionLabel}
          getOptionValue={field.getOptionValue}
          getOptionAvatarSeed={field.getOptionAvatarSeed}
          getOptionSubLabel={field.getOptionSubLabel}
          value={values[field.name] || []}
          onChange={(next) => handleChange(field.name, next)}
          placeholder={field.placeholder}
        />
      );
    }
    return (
      <Input
        id={field.name}
        type={field.type}
        value={values[field.name] || ''}
        onChange={(e) => handleChange(field.name, e.target.value)}
      />
    );
  };

  return (
    // Tried modal={false} here at one point — it fixes the same Radix
    // modality fight AsyncMultiSelectDropdown's other patches address (see
    // its own comments: manual wheel-scroll, pointer-events-auto), but it
    // broke opening this dialog from the row kebab menu entirely (a
    // DropdownMenuItem's own closing click gets misread as an "outside"
    // pointerdown on the newly non-modal Dialog, dismissing it in the same
    // tick) — confirmed live: the bulk-bar button trigger still opened it
    // fine, only the far more common kebab-menu path broke. Reverted;
    // AsyncMultiSelectDropdown's own focusin guard handles the remaining
    // fight (Radix's FocusScope yanking focus out of the portaled panel)
    // without touching this Dialog's modality at all.
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Per explicit request: a click outside shouldn't blow away
        // unsaved input on a form the user has actually started filling in
        // (see isDirtyRef above) — Escape and the explicit Cancel/X still
        // close it regardless, only the easy-to-trigger-by-accident
        // outside-click is guarded.
        onPointerDownOutside={(e) => {
          if (isDirtyRef.current) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>{schema.title}</DialogTitle>
          <DialogDescription>
            {schema.description} Applies to {candidateCount} selected candidate
            {candidateCount === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {schema.fields.map((field) => (
            <div key={field.name} className="space-y-2">
              <Label htmlFor={field.name}>{field.label}</Label>
              {renderField(field)}
            </div>
          ))}
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!isComplete || isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Working...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

ActionPayloadModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  schema: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    fields: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
        required: PropTypes.bool,
        minDate: PropTypes.instanceOf(Date),
        options: PropTypes.arrayOf(
          PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string,
          })
        ),
        fetcher: PropTypes.func,
        getOptionLabel: PropTypes.func,
        getOptionValue: PropTypes.func,
        getOptionAvatarSeed: PropTypes.func,
        getOptionSubLabel: PropTypes.func,
        placeholder: PropTypes.string,
      })
    ),
    buildPayload: PropTypes.func,
    prefillFromSchedule: PropTypes.bool,
  }),
  candidateCount: PropTypes.number,
  // Only used for reschedule_slot's prefill (see the `shouldPrefill` gate
  // above); null for a bulk fire.
  secMappingId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSubmitting: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
};

ActionPayloadModal.defaultProps = {
  schema: null,
  secMappingId: null,
  candidateCount: 0,
  isSubmitting: false,
};

export default ActionPayloadModal;
